import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Crown, Trophy, Zap, Sparkles, Loader2, Calendar, Repeat,
  XCircle, ExternalLink, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useAuth } from "@/contexts/AuthContext";
import { listMySubscriptions, cancelMySubscription } from "@/lib/api";
import { Seo } from "@/components/Seo";

const PLAN_ICONS = {
  standard: Zap, pro: Crown, pro_max: Trophy, free: Sparkles,
};

const PLAN_LABELS = {
  standard: "Standard", pro: "Pro", pro_max: "Pro Max", free: "Free",
};

const STATUS_COLORS = {
  ACTIVE: "border-emerald-500/40 text-emerald-500 bg-emerald-500/5",
  APPROVED: "border-emerald-500/40 text-emerald-500 bg-emerald-500/5",
  APPROVAL_PENDING: "border-amber-500/40 text-amber-500 bg-amber-500/5",
  CANCELLED: "border-muted-foreground/30 text-muted-foreground",
  EXPIRED: "border-muted-foreground/30 text-muted-foreground",
  SUSPENDED: "border-destructive/40 text-destructive bg-destructive/5",
};

export default function Billing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [subs, setSubs] = useState(null);
  const [cancellingId, setCancellingId] = useState("");

  useEffect(() => {
    if (!user) {
      navigate("/auth");
      return;
    }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, navigate]);

  const refresh = async () => {
    try {
      const res = await listMySubscriptions();
      setSubs(res.data?.subscriptions || []);
    } catch (e) {
      toast.error("Could not load subscriptions");
      setSubs([]);
    }
  };

  const handleCancel = async (sub) => {
    const confirmed = window.confirm(
      `Cancel your ${PLAN_LABELS[sub.plan]} ${sub.cadence || "monthly"} subscription? ` +
      `You'll keep access until ${user?.plan_expires_at ? new Date(user.plan_expires_at).toLocaleDateString() : "the end of the current billing period"}.`,
    );
    if (!confirmed) return;
    setCancellingId(sub.subscription_id);
    try {
      const res = await cancelMySubscription(sub.subscription_id);
      if (res.data?.ok) {
        toast.success("Subscription cancelled. You'll keep access until the period ends.");
        await refresh();
      } else {
        toast.error("PayPal didn't confirm the cancellation. Try again in a minute.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not cancel");
    } finally {
      setCancellingId("");
    }
  };

  const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");
  const currentPlan = user?.plan || "free";
  const PlanIcon = PLAN_ICONS[currentPlan] || Sparkles;
  const expiresAt = user?.plan_expires_at ? new Date(user.plan_expires_at).toLocaleDateString() : null;
  const activeSub = (subs || []).find((s) => ["ACTIVE", "APPROVED"].includes((s.status || "").toUpperCase()));

  return (
    <div className="min-h-screen bg-background grain">
      <Seo title="Billing & subscriptions — Sabzy AI" description="Manage your Sabzy AI subscription, view billing history, and cancel anytime." path="/billing" noindex />

      <header className="sticky top-0 z-40 glass border-b border-border">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-5 py-3.5 lg:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/chat")} data-testid="billing-back-btn">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <Logo />
          </div>
          <ThemeToggle />
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 py-10 lg:px-8 lg:py-16">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-widest text-primary">Account</div>
          <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">Billing & subscriptions</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Manage your active plan and view payment history. Cancellations take effect at the end of the current billing period — you keep access until then.
          </p>
        </div>

        {/* Current plan summary */}
        <div className="mb-8 rounded-2xl border border-border bg-card/50 p-6" data-testid="billing-current-plan">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="grid h-12 w-12 place-items-center rounded-xl bg-primary/15 text-primary">
                <PlanIcon className="h-6 w-6" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Active plan</div>
                <div className="mt-0.5 flex items-center gap-2">
                  <div className="text-2xl font-bold tracking-tight">{PLAN_LABELS[currentPlan]}</div>
                  {activeSub?.cadence && (
                    <Badge variant="outline" className="text-[10px] uppercase tracking-wider" data-testid="billing-cadence-badge">
                      {activeSub.cadence}
                    </Badge>
                  )}
                </div>
                {expiresAt && (
                  <div className="mt-1 text-xs text-muted-foreground">
                    {activeSub ? `Renews ${expiresAt}` : `Access until ${expiresAt}`}
                  </div>
                )}
              </div>
            </div>
            <Button variant="outline" onClick={() => navigate("/upgrade")} className="rounded-full" data-testid="billing-change-plan-btn">
              {currentPlan === "free" ? "Upgrade" : "Change plan"}
            </Button>
          </div>
        </div>

        {/* Subscriptions list */}
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold tracking-tight">Subscriptions</h2>
          <a
            href="https://www.paypal.com/myaccount/autopay/connect/payee?merchantId=sabzy"
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground transition hover:text-foreground"
            data-testid="paypal-manage-link"
          >
            Manage in PayPal <ExternalLink className="h-3 w-3" />
          </a>
        </div>

        {subs === null ? (
          <div className="flex h-32 items-center justify-center rounded-2xl border border-border bg-card/30 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : subs.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-10 text-center" data-testid="billing-no-subs">
            <Sparkles className="mx-auto mb-3 h-6 w-6 text-muted-foreground" />
            <div className="text-sm font-medium">No subscriptions yet</div>
            <div className="mt-1 text-xs text-muted-foreground">
              You're on the free tier (or a trial). Upgrade to unlock Pro Max racing mode.
            </div>
            <Button onClick={() => navigate("/upgrade")} className="mt-4 rounded-full" size="sm" data-testid="billing-cta-upgrade">
              See plans
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {subs.map((sub) => {
              const status = (sub.status || "").toUpperCase();
              const cancellable = ["ACTIVE", "APPROVED", "APPROVAL_PENDING"].includes(status);
              const Icon = PLAN_ICONS[sub.plan] || Sparkles;
              return (
                <div
                  key={sub.subscription_id}
                  className="rounded-xl border border-border bg-card/50 p-4 transition hover:bg-card"
                  data-testid={`subscription-${sub.subscription_id}`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="grid h-10 w-10 place-items-center rounded-lg bg-primary/15 text-primary">
                        <Icon className="h-4 w-4" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-semibold">{PLAN_LABELS[sub.plan] || sub.plan}</div>
                          <Badge variant="outline" className="gap-1 text-[10px] uppercase">
                            <Repeat className="h-2.5 w-2.5" /> {sub.cadence || "monthly"}
                          </Badge>
                          <Badge variant="outline" className={`text-[10px] uppercase ${STATUS_COLORS[status] || ""}`} data-testid={`sub-status-${sub.subscription_id}`}>
                            {status.replace("_", " ")}
                          </Badge>
                        </div>
                        <div className="mt-1 flex items-center gap-3 text-[11px] text-muted-foreground">
                          <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> Started {fmt(sub.activated_at || sub.created_at)}</span>
                          <span className="font-mono">{sub.subscription_id?.slice(0, 16)}…</span>
                        </div>
                      </div>
                    </div>
                    {cancellable && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCancel(sub)}
                        disabled={cancellingId === sub.subscription_id}
                        className="rounded-full border-destructive/40 text-destructive hover:bg-destructive/5"
                        data-testid={`cancel-sub-${sub.subscription_id}`}
                      >
                        {cancellingId === sub.subscription_id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <><XCircle className="mr-1.5 h-3.5 w-3.5" /> Cancel</>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-10 flex items-center justify-center gap-2 text-xs text-muted-foreground" data-testid="billing-trust">
          <ShieldCheck className="h-3.5 w-3.5" />
          Powered by PayPal · USD billing · cancel anytime · no card stored on Sabzy
        </div>
      </main>
    </div>
  );
}
